import React, {useCallback, useRef, useState} from 'react';
import {useClickOutside} from 'app/utils/useClickOutside';
import {getImageDomain, getLinkPath, scrollToTop} from 'app/utils';
import I18n from 'app/i18n';
import {Link} from 'react-router-dom';
import MultiLangsInterface from 'app/interfaces/MultiLangsInterface';
import PromoNewItemInterface from 'app/interfaces/articles/PromoNewItemInterface';
import {configStore} from 'app/store/global';
import {pushAnalyticsEvent} from 'app/utils/analytics';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import ParticipatePromoButton from 'app/components/reusable/ParticipatePromoButton';
import ClickableTooltip from 'app/components/reusable/ClickableTooltip';
import PromoNewCardTimer from 'app/components/pages/articles/PromoNewCardTimer';
import AnimatedParticipantsCount from 'app/components/pages/articles/AnimatedParticipantsCount';
import {getImageByBonusesType, getKeyByType} from 'app/components/my_profile/bonuses/bonuses_utils';

interface PromoNewCardProps {
    data: PromoNewItemInterface;
    hideExpiredPromoNewIndexItem: (id: number) => void;
}

const getLabelByLocale = (langs?: MultiLangsInterface): string => {
    if (!langs) {
        return '';
    }

    return langs[I18n.locale] || langs[configStore.DEFAULT_LOCALE] || '';
};

const getRewardText = (data: PromoNewItemInterface): string | null => {
    const reward = data.promo_bonus?.reward;

    if (!reward?.amount) {
        return null;
    }

    const amount = Number(reward.amount);
    const whole = Math.trunc(amount);
    const fraction = Math.trunc((amount - whole) * 100);

    if (reward.type === 'percent') {
        return `${whole}%`;
    }

    return fraction > 0 ? `$${whole}.${fraction}` : `$${whole}`;
};

const getBonusTooltipTitle = (type: string, birthday: boolean): string => {
    const key = getKeyByType(type, birthday);

    return `${I18n.t(key)} ${I18n.t('bonus').toLowerCase()}`;
};

// eslint-disable-next-line max-statements
const PromoNewCard = ({data, hideExpiredPromoNewIndexItem}: PromoNewCardProps): React.ReactElement => {
    const {
        id,
        preview_image,
        slug,
        promo_type,
        ended_at,
        promo_bonus,
        participants_count
    } = data;
    const cardTitle = getLabelByLocale(data.title_langs);
    const infoContent = getLabelByLocale(data.short_description_langs);
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const participation = promo_bonus?.participation;
    const canParticipate = Boolean(promo_bonus);
    const isParticipating = participation?.participating === true;
    const {
        turnover_required: turnoverRequired = 0,
        bets_turnover: turnoverCurrent = 0,
        percentage: progressPercentage
    } = participation?.progress ?? {};
    const turnoverProgressPercentage = turnoverRequired > 0
        ? Math.min(progressPercentage ?? turnoverCurrent / turnoverRequired * 100, 100)
        : 0;
    const rewardText = getRewardText(data);
    const rewardType = promo_bonus?.reward?.type;
    const rewardBirthday = promo_bonus?.reward?.birthday;

    const handlePromotionClick = useCallback(() => {
        pushAnalyticsEvent('promotion_click', {
            promotion_id: id,
            promotion_type: promo_type,
            source: 'promotion_list'
        });
        scrollToTop();
    }, [id, promo_type]);

    const handleInfoToggle = useCallback(() => {
        setIsInfoOpen(prevOpen => !prevOpen);
    }, []);

    const handleExpire = useCallback(() => {
        hideExpiredPromoNewIndexItem(id);
    }, [hideExpiredPromoNewIndexItem, id]);

    useClickOutside(cardRef, handleInfoToggle, isInfoOpen);

    return (
        <div
            ref={cardRef}
            className={`promo-new-card${isInfoOpen ? ' promo-new-card--info-open' : ''}${isParticipating ? ' promo-new-card--participating' : ''}`}
        >
            <div className="promo-new-card__image-wrap">
                {preview_image && <img
                    src={getImageDomain(false) + preview_image}
                    className="promo-new-card__image"
                    alt={cardTitle}
                />}
                <div className="promo-new-card__overlay">
                    <div className="promo-new-card__overlay-inner">
                        <span className="promo-new-card__badge">
                            {I18n.t(`promo_new_header_${promo_type}`)}
                        </span>
                        {ended_at && <PromoNewCardTimer endTime={ended_at} onExpire={handleExpire} />}
                    </div>
                </div>
            </div>
            <div className="promo-new-card__body">
                <h3 className="promo-new-card__title">{cardTitle}</h3>
                {rewardText &&
                    <div className="promo-new-card__prise">
                        <span dangerouslySetInnerHTML={{__html: rewardText}} />
                        {rewardType &&
                            <ClickableTooltip
                                className="promo-new-card__bonus-tooltip"
                                text={
                                    <div className="promo-new-card__bonus-tooltip-content">
                                        <p className="promo-new-card__bonus-tooltip-title">
                                            {getBonusTooltipTitle(rewardType, rewardBirthday)}
                                        </p>
                                        <p className="promo-new-card__bonus-tooltip-desc">
                                            {I18n.t('bonuses_page.bonus_tooltip_desc')}
                                        </p>
                                        <Link
                                            to={getLinkPath('/faq/bonus')}
                                            className="promo-new-card__bonus-tooltip-link"
                                        >
                                            {I18n.t('read_more_u')}
                                        </Link>
                                    </div>
                                }
                            >
                                <img
                                    src={getImageByBonusesType(rewardType, rewardBirthday)}
                                    alt={getKeyByType(rewardType, rewardBirthday)}
                                />
                            </ClickableTooltip>
                        }
                    </div>
                }
                {turnoverRequired > 0 &&
                    <div className="promo-new-card__turnover">
                        {isParticipating
                            ? <>
                                <div className="promo-new-card__turnover-header promo-new-card__turnover-header--participating">
                                    <span>
                                        {I18n.t('bonuses_page.turnover')}
                                    </span>
                                    <p className="promo-new-card__turnover-value">
                                        <span>${Math.trunc(turnoverCurrent)}</span>
                                        {' / '}
                                        ${Math.trunc(turnoverRequired)}
                                    </p>
                                </div>
                                <div className="promo-new-card__turnover-progress">
                                    <div
                                        className="promo-new-card__turnover-progress-fill"
                                        style={{width: `${turnoverProgressPercentage}%`}}
                                    />
                                </div>
                            </>
                            : <div className="promo-new-card__turnover-header">
                                <span>
                                    {I18n.t('bonuses_page.turnover')} ${Math.trunc(turnoverRequired)}
                                </span>
                            </div>
                        }
                    </div>
                }
                <div className="promo-new-card__footer">
                    <div className="promo-new-card__participate">
                        <div className="promo-new-card__participants">
                            <SpriteIcon name="#svg-account" size={{height: 16, width: 16}} />
                            <AnimatedParticipantsCount value={participants_count ?? 0} />
                        </div>
                        {isParticipating
                            ? <div className="promo-new-card__participating-status">
                                <SpriteIcon
                                    name="#svg-check"
                                    size={{height: 11, width: 11}}
                                />
                                <span>{I18n.t('bonuses_page.you_are_participating')}</span>
                            </div>
                            : canParticipate && <ParticipatePromoButton
                                isParticipating={isParticipating}
                                promoId={id}
                                title={cardTitle}
                                authPlacement="PROMO_NEW_CARD"
                                className="promo-new-card__btn"
                            />
                        }
                    </div>
                    <button
                        type="button"
                        className="promo-new-card__info"
                        onClick={handleInfoToggle}
                    >
                        <SpriteIcon name="#svg-info-transparent" size={{height: 16, width: 16}} />
                    </button>
                </div>
            </div>
            <div className={`promo-new-card__info-panel${isInfoOpen ? ' promo-new-card__info-panel--visible' : ''}`}
            >
                <h4 className="promo-new-card__info-panel-title">{cardTitle}</h4>
                <div className="promo-new-card__info-panel-content" dangerouslySetInnerHTML={{__html: infoContent}} />
                <Link
                    to={getLinkPath(`/promotions/${slug}`)}
                    className="promo-new-card__info-panel-link"
                    onClick={handlePromotionClick}
                >
                    {I18n.t('full_description')}
                </Link>
                <button
                    type="button"
                    className="promo-new-card__info-panel-close"
                    onClick={handleInfoToggle}
                >
                    <SpriteIcon name="#svg-close" size={{height: 10, width: 10}} />
                </button>
            </div>
        </div>
    );
};

export default PromoNewCard;
